/**
 * BooleanSearch Component
 * A query builder for creating complex search criteria with AND/OR groups.
 * 
 * @see specifications/Components/Boolean-Search.md - Full documentation
 * @see src/forms/samples/booleanSearch.ts - Sample form demonstrating usage
 * 
 * Features:
 * - AND/OR condition groups
 * - Nested groups for complex boolean logic
 * - Field selection from FieldDescriptions (Standing Data)
 * - Dynamic operators based on field type
 * - Multi-value selection with chips (for 'in' operator)
 * - Outputs SearchTreeJson format for TriSys Web API
 * 
 * API Output Format:
 * {
 *   "EntityName": "Company",
 *   "RecordId": 0,
 *   "Tree": {
 *     "condition": "AND",
 *     "rules": [
 *       { "id": "Sector", "field": "Sector", "type": "string", "input": "select", "operator": "in", "value": ["Commercial", "For Hire"] },
 *       { "condition": "OR", "rules": [...] }
 *     ]
 *   }
 * }
 * 
 * @example
 * ```typescript
 * const booleanSearch = new BooleanSearch(container, {
 *   entityName: 'Company',
 *   entitySearchId: 53,
 *   fields: [...],  // From FieldDescriptions
 *   lookups: {...}, // Field ID -> lookup values
 *   onSearch: (json) => console.log(json)
 * });
 * await booleanSearch.initialize();
 * ```
 */

import { IComponent } from './IComponent';
import { Logger } from '@/services/Logger';
import { DatePicker } from '@/components/DatePicker';
import { TaskTypeDropdown } from '@/components/TaskTypeDropdown';
import { CurrencyAmountPeriod, CurrencyAmountPeriodValue } from '@/components/CurrencyAmountPeriod';

const logger = Logger.scope('BooleanSearch');

// ===== Types =====

/**
 * Operators available for different field types
 */
export type BooleanOperator = 
  | 'equal' | 'not_equal'
  | 'in' | 'not_in'
  | 'less' | 'less_or_equal'
  | 'greater' | 'greater_or_equal'
  | 'between' | 'not_between'
  | 'begins_with' | 'not_begins_with'
  | 'contains' | 'not_contains'
  | 'ends_with' | 'not_ends_with'
  | 'is_empty' | 'is_not_empty'
  | 'is_null' | 'is_not_null'
  // Date-specific operators
  | 'before' | 'after' | 'on' | 'not_on';

/**
 * Field type mapping for operators
 */
export type FieldType = 'string' | 'integer' | 'double' | 'date' | 'time' | 'datetime' | 'boolean';

/**
 * Input type for field values
 */
export type InputType = 'text' | 'number' | 'textarea' | 'select' | 'radio' | 'checkbox';

/**
 * Field definition for the query builder
 */
export interface BooleanSearchField {
  id: string;          // Field identifier (TableFieldName)
  label: string;       // Display label (FieldLabel)
  type: FieldType;     // Data type for operator selection
  fieldTypeId?: number; // Original FieldTypeId from Standing Data (2=integer, 3=double, 4/5=date, 6=boolean)
  input?: InputType;   // Input type for value entry
  values?: { value: string | number; label: string }[]; // For select/checkbox fields - value can be string or number
  operators?: BooleanOperator[];  // Allowed operators (auto-determined if not provided)
  default_operator?: BooleanOperator;
  default_value?: string | string[] | number | boolean;
  validation?: {
    min?: number;
    max?: number;
    step?: number;
    format?: string;  // Regex pattern
  };
  /** If true, this is the special Task field requiring composite widget */
  isTaskField?: boolean;
  /** If true, this is a generic skill field (FieldId: 0) that can apply to any entity */
  isGenericSkill?: boolean;
  /** If true, this is an Address Radius field for UK postcode radius search */
  isAddressRadiusField?: boolean;
  /** If true, this is a CurrencyAmountPeriod field (FieldTypeId: 39) */
  isCurrencyAmountPeriodField?: boolean;
}

/**
 * A single rule in the query tree
 */
export interface BooleanSearchRule {
  id: string;
  field: string;
  type: FieldType;
  input: InputType;
  operator: BooleanOperator;
  value: string | string[] | number | boolean | null;
  /** Optional data object for complex fields (e.g., PostCodeRadial for Address Radius) */
  data?: Record<string, unknown>;
}

/**
 * A group of rules with AND/OR condition
 */
export interface BooleanSearchGroup {
  condition: 'AND' | 'OR';
  rules: (BooleanSearchRule | BooleanSearchGroup)[];
}

/**
 * The complete search tree JSON structure
 */
export interface SearchTreeJson {
  EntityName: string;
  RecordId: number;
  Tree: BooleanSearchGroup;
}

/**
 * Operating mode for the BooleanSearch component.
 * 
 * - `TriSysWebAPI`: Component builds fields internally from StandingData cache.
 *   Just provide entityName and the component handles everything.
 * 
 * - `Custom`: Consuming form provides all fields and lookup values manually.
 *   Use this for non-TriSys integrations or custom field sets.
 */
export type BooleanSearchMode = 'TriSysWebAPI' | 'Custom';

/**
 * Component options
 */
export interface BooleanSearchOptions {
  /** 
   * Operating mode (default: 'TriSysWebAPI')
   * - 'TriSysWebAPI': Fields built from standingData automatically
   * - 'Custom': Fields provided by consuming form
   */
  mode?: BooleanSearchMode;
  
  /** Entity name (e.g., "Company", "Contact") */
  entityName: string;
  
  /** Entity search ID for the API */
  entitySearchId: number;
  
  /** 
   * Available fields for searching.
   * - In 'TriSysWebAPI' mode: Optional, fields built from standingData
   * - In 'Custom' mode: Required, consuming form provides all fields
   */
  fields?: BooleanSearchField[];
  
  /**
   * Standing data cache from TriSys Web API.
   * Required for 'TriSysWebAPI' mode.
   * The component will build fields, lookups, and cross-entity fields from this.
   */
  standingData?: StandingDataResponse;
  
  /** Callback when search is triggered */
  onSearch?: (searchTree: SearchTreeJson | null) => void;
  /** Callback when tree structure changes */
  onChange?: (searchTree: SearchTreeJson | null) => void;
  /** Initial tree state to restore */
  initialTree?: BooleanSearchGroup;
  /** Compact mode (smaller UI) */
  compact?: boolean;
  /** Show search button */
  showSearchButton?: boolean;
  /** Show clear button */
  showClearButton?: boolean;
  /** Auto-search on change */
  autoSearch?: boolean;
  /** Debounce delay for auto-search (ms) */
  debounceDelay?: number;
}

// Import StandingDataResponse type for TriSysWebAPI mode
import type { StandingDataResponse } from '@/services/api/TriSysWebAPI';

/**
 * Operator definitions with labels and applicable types
 */
const OPERATORS: Record<BooleanOperator, { label: string; types: FieldType[]; nb_inputs: number }> = {
  'equal': { label: 'equals', types: ['string', 'integer', 'double', 'date', 'time', 'datetime', 'boolean'], nb_inputs: 1 },
  'not_equal': { label: 'not equals', types: ['string', 'integer', 'double', 'date', 'time', 'datetime', 'boolean'], nb_inputs: 1 },
  'in': { label: 'in', types: ['string', 'integer', 'double'], nb_inputs: 1 },
  'not_in': { label: 'not in', types: ['string', 'integer', 'double'], nb_inputs: 1 },
  'less': { label: 'less than', types: ['integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 1 },
  'less_or_equal': { label: 'less or equal', types: ['integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 1 },
  'greater': { label: 'greater than', types: ['integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 1 },
  'greater_or_equal': { label: 'greater or equal', types: ['integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 1 },
  'between': { label: 'between', types: ['integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 2 },
  'not_between': { label: 'not between', types: ['integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 2 },
  'begins_with': { label: 'begins with', types: ['string'], nb_inputs: 1 },
  'not_begins_with': { label: 'not begins with', types: ['string'], nb_inputs: 1 },
  'contains': { label: 'contains', types: ['string'], nb_inputs: 1 },
  'not_contains': { label: 'not contains', types: ['string'], nb_inputs: 1 },
  'ends_with': { label: 'ends with', types: ['string'], nb_inputs: 1 },
  'not_ends_with': { label: 'not ends with', types: ['string'], nb_inputs: 1 },
  'is_empty': { label: 'is empty', types: ['string'], nb_inputs: 0 },
  'is_not_empty': { label: 'is not empty', types: ['string'], nb_inputs: 0 },
  'is_null': { label: 'is null', types: ['string', 'integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 0 },
  'is_not_null': { label: 'is not null', types: ['string', 'integer', 'double', 'date', 'time', 'datetime'], nb_inputs: 0 },
  // Date-specific operators (friendlier names than less/greater for dates)
  'before': { label: 'before', types: ['date', 'time', 'datetime'], nb_inputs: 1 },
  'after': { label: 'after', types: ['date', 'time', 'datetime'], nb_inputs: 1 },
  'on': { label: 'on', types: ['date', 'time', 'datetime'], nb_inputs: 1 },
  'not_on': { label: 'not on', types: ['date', 'time', 'datetime'], nb_inputs: 1 }
};

/**
 * Fields to hide from search criteria.
 * These are FieldDescriptions that exist but should not be displayed in the query builder.
 * Format: "TableName.TableFieldName"
 * 
 * @see ctrlSearchCriteria.js in Apex for the original implementation
 */
export function getHiddenSearchFields(): string[] {
  return [
    // Gender - use Gender skill instead of these boolean fields
    'Contact.ContactFemale',
    'Contact.ContactMale',
    
    // Legacy/deprecated fields
    'Contact.ContactMarried',  // Use MaritalStatus skill instead
    
    // Internal fields not useful for searching
    'Contact.SurnameInitial',  // Derived from Surname
    
    // Placement-related fields that don't belong on Contact search
    'Contact.Placement',
    'Contact.PlacementDocument',
    'Contact.PlacementStages',
    
    // Fields that are cross-entity or have special handling
    'Contact.Qualifications',   // Cross-entity field handled separately
    'Contact.SuitableFor',      // Not applicable to Contact search
    'Contact.TempJobTitle',     // Use JobTitle skill instead
  ];
}

/**
 * Entity field configuration for search criteria.
 * Defines which FieldDescription fields are allowed/ignored per entity.
 * 
 * @see specifications/Components/search-criteria-entity-fields.json
 * @see ctrlSearchCriteria.AllowedTableFieldNamesForEntity() in Apex
 */
export interface EntityFieldConfig {
  EntityName: string;
  AllowedTableFieldNames: string[];
  IgnoredLookupTableFieldNames: string[];
}

/**
 * Configuration data for allowed fields per entity.
 * Fields not in AllowedTableFieldNames are excluded from search.
 * Lookup fields in IgnoredLookupTableFieldNames are also excluded.
 */
const ENTITY_FIELD_CONFIG: EntityFieldConfig[] = [
  {
    EntityName: 'Contact',
    AllowedTableFieldNames: [
      'Forenames',
      'Surname',
      'AvailabilityDate',
      'Comments',
      'CurrentRemuneration',
      'RequiredRemunerationContract',
      'RequiredRemunerationPermanent',
      'CurrentEmployer',
      'CVLastUpdated',
      'DateFirstRegistered',
      'DateLastUpdated',
      'DateOfNextContact',
      'DateLastContacted',
      'DateOfBirth',
      'Department',
      'PlacementStartDate',
      'VisaExpiry',
      'Expiry',
      'SyncWithOutlook',
      'Priority',      // Contact priority lookup (uses ContactPriorities)
      'JobTitle',      // Job title lookup
      'JobTitleId',    // Alternative field name for Job Title
      'Type',          // Entity type (uses EntityTypes)
      'EntityTypeId',  // Alternative field name for Type
      'User'           // User lookup (uses Users cache, ContactUser table)
      // TODO: Also add 'User' to Requirement and Placement entities when needed
    ],
    IgnoredLookupTableFieldNames: [
      'Users'
    ]
  },
  {
    EntityName: 'Company',
    AllowedTableFieldNames: [
      // IMPORTANT: Use just the TableFieldName (not prefixed with TableName_)
      // The filter extracts the field name from "TableName_FieldName" format
      'Comments',
      'AccountingID',
      'Name',
      'Turnover',
      'WebPage',
      'Industry',  // Note: IndustryId field uses Company_Industry as field ID
      'MainTelNo',
      'MainFaxNo',
      'EMail',
      'Employees',
      'Directors',
      'RegistrationNumber',
      'VATNumber',
      'Sector',
      'Source',
      'CompanyStatus',
      'CompanyType',
      'UKRegion',
      'PaymentTerms',
      'CreditCheckLimit',
      'DateLastUpdated',
      'CompanyType',           // Entity type (uses EntityTypes) - Apex format: no underscore
      'CompanyDivision'
    ],
    IgnoredLookupTableFieldNames: [
      'CreditCheckStatus',
      'CompanyType'
    ]
  },
  {
    EntityName: 'Requirement',
    AllowedTableFieldNames: [
      'DateInitiated',
      'EarliestStartDate',
      'Bonus',
      'Comments',
      'DisplayOnWeb',
      'DurationInWeeks',
      'Fee',
      'FeePercentage',
      'FixedTerm',
      'FixedTermDurationWeeks',
      'MaximumSalary',
      'NumberRequired',
      'ProbabilityToFill',
      'RequirementProfit',
      'RequirementValue',
      'TotalPackageValue',
      'WebText',
      'AWRInScope',
      'Type',          // Entity type (uses EntityTypes)
      'EntityTypeId'   // Alternative field name for Type
    ],
    IgnoredLookupTableFieldNames: [
      'PermanentType',
      'TemplateDeliveryMechanism',
      'InterviewLocation',
      'InterviewStage',
      'FollowUpTime'
    ]
  },
  {
    EntityName: 'Placement',
    AllowedTableFieldNames: [
      'DateInitiated',
      'StartDate',
      'EndDate',
      'ExtensionNumber',
      'Fee',
      'FeePercentage',
      'FixedTerm',
      'FixedTermDurationWeeks',
      'FlatFee',
      'OriginalStartDate',
      'OriginalEndDate',
      'PlacementPONumber',
      'Salary',
      'Terminated',
      'TotalPackageValue',
      'AWRInScope',
      'AWRPlacement',
      'Type',          // Entity type (uses EntityTypes)
      'EntityTypeId'   // Alternative field name for Type
    ],
    IgnoredLookupTableFieldNames: [
      'PermanentType',
      'TemplateDeliveryMechanism',
      'InterviewLocation',
      'InterviewStage',
      'FollowUpTime'
    ]
  }
];

/**
 * Get the field configuration for an entity.
 * @param entityName - The entity name (e.g., "Contact", "Company")
 * @returns The configuration or undefined if not found
 */
export function getEntityFieldConfig(entityName: string): EntityFieldConfig | undefined {
  return ENTITY_FIELD_CONFIG.find(c => c.EntityName === entityName);
}

/**
 * Check if a field is allowed for searching in an entity.
 * If no configuration exists for the entity, all fields are allowed.
 * 
 * @param entityName - The entity name
 * @param fieldId - The TableFieldName from FieldDescriptions
 * @returns true if the field is allowed
 */
export function isFieldAllowedForSearch(entityName: string, fieldId: string): boolean {
  const config = getEntityFieldConfig(entityName);
  if (!config) {
    // No configuration = allow all fields
    return true;
  }
  // Extract just the TableFieldName from the id (format: TableName_FieldName)
  const tableFieldName = fieldId.includes('_') ? fieldId.split('_').pop()! : fieldId;
  return config.AllowedTableFieldNames.includes(tableFieldName);
}

/**
 * Check if a lookup field should be ignored for an entity.
 * 
 * @param entityName - The entity name
 * @param fieldId - The TableFieldName from FieldDescriptions
 * @returns true if the field should be ignored
 */
export function isLookupFieldIgnored(entityName: string, fieldId: string): boolean {
  const config = getEntityFieldConfig(entityName);
  if (!config) {
    return false;
  }
  // Extract just the TableFieldName from the id (format: TableName_FieldName)
  const tableFieldName = fieldId.includes('_') ? fieldId.split('_').pop()! : fieldId;
  return config.IgnoredLookupTableFieldNames.includes(tableFieldName);
}

/**
 * Cross-entity search fields to add.
 * These fields allow searching across related entities (e.g., find placements for a specific contact).
 * The TriSys Web API handles these special field IDs server-side.
 * 
 * Format: { id: 'FieldId', label: 'Display Label', type: FieldType, entities: ['Entity1', 'Entity2'] }
 * 
 * @see ctrlSearchCriteria.EntityFields and AddCrossEntitySearchFields in Apex for the original implementation
 */
export interface CrossEntityField {
  id: string;
  label: string;
  type: FieldType;
  entities: string[];  // Which entities this field applies to
  input?: InputType;
  operators?: BooleanOperator[];
  /** If true, this is the special Task field requiring composite widget */
  isTaskField?: boolean;
  /** If true, this is the special Address Radius field requiring composite widget */
  isAddressRadiusField?: boolean;
  /** If true, this is a CurrencyAmountPeriod field (FieldTypeId: 39) */
  isCurrencyAmountPeriodField?: boolean;
}

export function getCrossEntitySearchFields(): CrossEntityField[] {
  return [
    // ===== GENERIC FIELDS (all or most entities) =====
    
    // Entity ID - available for all entities (uses EntityIdSuffix pattern: {EntityName}_Entity_Id)
    { id: 'Contact_Entity_Id', label: 'ID', type: 'integer', entities: ['Contact'] },
    { id: 'Company_Entity_Id', label: 'ID', type: 'integer', entities: ['Company'] },
    { id: 'Requirement_Entity_Id', label: 'ID', type: 'integer', entities: ['Requirement'] },
    { id: 'Placement_Entity_Id', label: 'ID', type: 'integer', entities: ['Placement'] },
    { id: 'Timesheet_Entity_Id', label: 'ID', type: 'integer', entities: ['Timesheet'] },
    { id: 'Invoice_Entity_Id', label: 'ID', type: 'integer', entities: ['Invoice'] },
    { id: 'Task_Entity_Id', label: 'ID', type: 'integer', entities: ['Task'] },
    
    // Task link - available for all entities except Task itself
    { id: 'TaskLink', label: 'Has Task', type: 'string', entities: ['Contact', 'Company', 'Requirement', 'Placement', 'Timesheet'] },
    
    // ===== COMPANY FIELDS (cross-entity for Contact, Requirement, Placement) =====
    
    { id: 'Company_CompanyName', label: 'Company Name', type: 'string', entities: ['Contact', 'Requirement', 'Placement'] },
    { id: 'Company_AddressCity', label: 'Company Address City', type: 'string', entities: ['Contact', 'Company', 'Requirement', 'Placement'] },
    { id: 'Company_AddressCounty', label: 'Company Address County', type: 'string', entities: ['Contact', 'Company', 'Requirement', 'Placement'] },
    { id: 'Company_AddressCountry', label: 'Company Address Country', type: 'string', entities: ['Contact', 'Company', 'Requirement', 'Placement'] },
    
    // ===== CONTACT NAME FIELDS (cross-entity) =====
    
    { id: 'ClientContact_FullName', label: 'Client Contact Full Name', type: 'string', entities: ['Requirement', 'Placement'] },
    { id: 'CandidateContact_FullName', label: 'Candidate Contact Full Name', type: 'string', entities: ['Placement'] },
    // Note: Contact_FullName removed from Company - use Company_Name field instead
    { id: 'Contact_FullName', label: 'Contact Name', type: 'string', entities: ['Requirement', 'Placement'] },
    
    // ===== CONTACT-SPECIFIC FIELDS =====
    
    // Home Address
    { id: 'Home_AddressCity', label: 'Home Address City', type: 'string', entities: ['Contact'] },
    { id: 'Home_AddressCounty', label: 'Home Address County', type: 'string', entities: ['Contact'] },
    { id: 'Home_AddressCountry', label: 'Home Address Country', type: 'string', entities: ['Contact'] },
    
    // CV Free Text Search
    { id: 'Contact_CVSearch', label: 'CV Free Text Search', type: 'string', entities: ['Contact'], operators: ['contains', 'not_contains'] },
    
    // ===== BASE CONTACT TABLE FIELDS =====
    // IMPORTANT: These use the EXACT field IDs that Apex sends to the API
    // e.g., "Contact_Christian" not "Contact_Forenames" - legacy naming from Apex
    { id: 'Contact_Christian', label: 'Forenames', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'ends_with', 'is_null', 'is_not_null'] },
    { id: 'Contact_Surname', label: 'Surname', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'ends_with', 'is_null', 'is_not_null'] },
    { id: 'Contact_FullName', label: 'Full Name', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'ends_with'] },
    { id: 'Contact_ContactType', label: 'Contact Type', type: 'string', entities: ['Contact'], operators: ['equal', 'not_equal', 'in', 'not_in'] },
    { id: 'Contact_Priority', label: 'Priority', type: 'string', entities: ['Contact'], operators: ['equal', 'not_equal', 'in', 'not_in'] },
    { id: 'Contact_Status', label: 'Status', type: 'string', entities: ['Contact'], operators: ['equal', 'not_equal', 'in', 'not_in', 'is_null', 'is_not_null'] },
    { id: 'Contact_CompanyName', label: 'Company Name', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'ends_with', 'is_null', 'is_not_null'] },
    { id: 'Contact_JobTitle', label: 'Job Title', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'ends_with', 'is_null', 'is_not_null'] },
    { id: 'Contact_Department', label: 'Department', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_WorkEMail', label: 'Work Email', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'ends_with', 'is_null', 'is_not_null'] },
    { id: 'Contact_PersonalEMail', label: 'Personal Email', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'not_equal', 'ends_with', 'is_null', 'is_not_null'] },
    { id: 'Contact_MobileTelNo', label: 'Mobile', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_WorkTelNo', label: 'Work Tel', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_HomeTelNo', label: 'Home Tel', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_HomeAddressPostCode', label: 'Home Postcode', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_HomeAddressCity', label: 'Home City', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_HomeAddressCounty', label: 'Home County', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_HomeAddressCountry', label: 'Home Country', type: 'string', entities: ['Contact'], operators: ['begins_with', 'contains', 'equal', 'is_null', 'is_not_null'] },
    { id: 'Contact_DateFirstRegistered', label: 'Date First Registered', type: 'date', entities: ['Contact'] },
    { id: 'Contact_DateLastUpdated', label: 'Date Last Updated', type: 'date', entities: ['Contact'] },
    { id: 'Contact_DateLastContacted', label: 'Date Last Contacted', type: 'date', entities: ['Contact'] },
    { id: 'Contact_DateOfNextContact', label: 'Date of Next Contact', type: 'date', entities: ['Contact'] },
    { id: 'Contact_DateOfBirth', label: 'Date of Birth', type: 'date', entities: ['Contact'] },
    
    // Radius/Postcode searches (Contact-specific address types)
    { id: 'HomeAddressRadius', label: 'Home Address Radius', type: 'string', entities: ['Contact'],
      operators: ['less', 'less_or_equal', 'greater', 'greater_or_equal'], isAddressRadiusField: true },
    { id: 'WorkAddressRadius', label: 'Work Address Radius', type: 'string', entities: ['Contact'],
      operators: ['less', 'less_or_equal', 'greater', 'greater_or_equal'], isAddressRadiusField: true },
    
    // Any Telephone/Email - aggregated search across multiple fields
    { id: 'AnyTelephoneNumber', label: 'Telephone Number', type: 'string', entities: ['Contact', 'Company'], operators: ['begins_with', 'ends_with', 'contains', 'is_null'] },
    { id: 'AnyEMailAddress', label: 'E-Mail Address', type: 'string', entities: ['Contact', 'Company'], operators: ['begins_with', 'ends_with', 'contains', 'equal', 'not_equal', 'is_null', 'is_not_null'] },
    
    // GDPR fields (Contact only)
    { id: 'AutomatedNotificationEMailSent', label: 'Automated Notification EMail Sent', type: 'boolean', entities: ['Contact'] },
    { id: 'AgreeToMarketingCommunications', label: 'Agreed To Marketing Communications', type: 'boolean', entities: ['Contact'] },
    { id: 'DeletionRequestReceived', label: 'Deletion Request Received', type: 'boolean', entities: ['Contact'] },
    { id: 'RestrictDataProcessingRequested', label: 'Restrict Data Processing Requested', type: 'boolean', entities: ['Contact'] },
    { id: 'AgreedToPrivacyPolicy', label: 'Agreed to Privacy Policy', type: 'boolean', entities: ['Contact'] },
    { id: 'DataPortabilityRequestReceived', label: 'Data Portability Request Received', type: 'boolean', entities: ['Contact'] },
    
    // Social Media (Contact only in Apex, but could apply to Company too)
    { id: 'LinkedIn', label: 'LinkedIn', type: 'string', entities: ['Contact'], operators: ['is_not_null', 'is_null', 'contains'] },
    { id: 'Twitter', label: 'Twitter', type: 'string', entities: ['Contact'], operators: ['is_not_null', 'is_null', 'contains'] },
    { id: 'Facebook', label: 'Facebook', type: 'string', entities: ['Contact'], operators: ['is_not_null', 'is_null', 'contains'] },
    { id: 'YouTube', label: 'YouTube', type: 'string', entities: ['Contact'], operators: ['is_not_null', 'is_null', 'contains'] },
    { id: 'Instagram', label: 'Instagram', type: 'string', entities: ['Contact'], operators: ['is_not_null', 'is_null', 'contains'] },
    { id: 'Pinterest', label: 'Pinterest', type: 'string', entities: ['Contact'], operators: ['is_not_null', 'is_null', 'contains'] },
    
    // ===== COMPANY-SPECIFIC FIELDS =====
    
    /* ===== TRISYS CRM FIELDS ONLY =====
    
    { id: 'CustomerAtAnyTime', label: 'Customer at any time', type: 'boolean', entities: ['Contact', 'Company'] },
    { id: 'CompanyOKtoService', label: 'Company OK to Service', type: 'boolean', entities: ['Company'] }, */
    
    // ===== RECRUITMENT COUNT FIELDS (Contact and Company) =====
    
    { id: 'RequirementClientCount', label: 'Requirement (Client) Count', type: 'integer', entities: ['Contact'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'RequirementClientCount', label: 'Requirement Count', type: 'integer', entities: ['Company'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'PlacementClientCount', label: 'Placement (Client) Count', type: 'integer', entities: ['Contact'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'PlacementClientCount', label: 'Placement Count', type: 'integer', entities: ['Company'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'RequirementCandidateCount', label: 'Requirement (Candidate) Count', type: 'integer', entities: ['Contact'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'PlacementCandidateCount', label: 'Placement (Candidate) Count', type: 'integer', entities: ['Contact'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'PlacementCount', label: 'Placement Count', type: 'integer', entities: ['Company'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'TimesheetCount', label: 'Timesheet Count', type: 'integer', entities: ['Contact', 'Company'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    { id: 'TaskCount', label: 'Task Count', type: 'integer', entities: ['Company'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'equal', 'not_equal'] },
    
    // Task - special field for Contact (complex search with TaskType + date range)
    // Value format: "TaskTypeName,startDate,endDate" (e.g., "Birthday,2025/01/01,2025/12/31")
    { id: 'Task', label: 'Task', type: 'string', entities: ['Contact'], operators: ['in', 'not_in'], isTaskField: true },
    
    // User field - from junction tables (ContactUser, RequirementUser, PlacementUser)
    // Values populated from Users cache, displays FullName but passes UserId to backend
    { id: 'User', label: 'User', type: 'string', entities: ['Contact'] },
    // TODO: Add User to Requirement and Placement when needed:
    // { id: 'User', label: 'User', type: 'string', entities: ['Requirement', 'Placement'] },
    
    // ===== REQUIREMENT & PLACEMENT FIELDS =====
    
    // Address Radius - special UK postcode radius search
    // Value format: "distance,postcode" (e.g., "3,cb1")
    // PostCodeRadial in data object mirrors the value
    { id: 'SiteAddressRadius', label: 'Address Radius', type: 'string', entities: ['Company', 'Contact', 'Requirement', 'Placement'], 
      operators: ['less', 'less_or_equal', 'greater', 'greater_or_equal'], isAddressRadiusField: true },
    { id: 'Reference', label: 'Reference', type: 'string', entities: ['Requirement', 'Placement'] },
    { id: 'StandardPayRate', label: 'Standard Pay Rate', type: 'double', entities: ['Requirement', 'Placement'], operators: ['less', 'less_or_equal', 'greater', 'greater_or_equal'] },
    { id: 'StandardChargeRate', label: 'Standard Charge Rate', type: 'double', entities: ['Requirement', 'Placement'], operators: ['less', 'less_or_equal', 'greater', 'greater_or_equal'] },
    
    // ===== REQUIREMENT-SPECIFIC FIELDS =====
    
    { id: 'TotalShortlisted', label: 'Total Shortlisted', type: 'integer', entities: ['Requirement'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'equal', 'not_equal'] },
    { id: 'CVSentCount', label: 'CV Sent Count', type: 'integer', entities: ['Requirement'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'equal', 'not_equal'] },
    { id: 'InterviewCount', label: 'Interview Count', type: 'integer', entities: ['Requirement'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'equal', 'not_equal'] },
    { id: 'Placed', label: 'Placed', type: 'integer', entities: ['Requirement'], operators: ['greater', 'greater_or_equal', 'less', 'less_or_equal', 'equal', 'not_equal'] },
    
    // ===== TIMESHEET-SPECIFIC FIELDS =====
    
    { id: 'PlacementReference', label: 'Placement Reference', type: 'string', entities: ['Timesheet'] },
    { id: 'Company', label: 'Company Name', type: 'string', entities: ['Timesheet'] },
    { id: 'JobTitle', label: 'Job Title', type: 'string', entities: ['Timesheet'] },
    { id: 'Candidate', label: 'Candidate Name', type: 'string', entities: ['Timesheet'] },
    { id: 'InputDate', label: 'Input Date', type: 'date', entities: ['Timesheet'] },
    { id: 'Description', label: 'Period', type: 'string', entities: ['Timesheet'] },
    { id: 'PeriodRange', label: 'Period Range', type: 'date', entities: ['Timesheet'] },
    { id: 'TotalTimeWorked', label: 'Total Time Worked', type: 'integer', entities: ['Timesheet'] },
    { id: 'PaymentAuthorised', label: 'Authorised', type: 'boolean', entities: ['Timesheet'] },
    { id: 'Payrolled', label: 'Paid', type: 'boolean', entities: ['Timesheet'] },
  ];
}

/**
 * Get cross-entity fields filtered for a specific entity
 */
export function getCrossEntityFieldsForEntity(entityName: string): BooleanSearchField[] {
  return getCrossEntitySearchFields()
    .filter(field => field.entities.includes(entityName))
    .map(field => ({
      id: field.id,
      label: field.label,
      type: field.type,
      input: field.input || 'text',
      operators: field.operators,
      isTaskField: field.isTaskField,
      isAddressRadiusField: field.isAddressRadiusField,
      isCurrencyAmountPeriodField: field.isCurrencyAmountPeriodField
    }));
}

/**
 * Check if a field should be hidden from search
 * @param tableName - The table name (e.g., "Contact")
 * @param fieldName - The field name (e.g., "ContactFemale")
 */
export function isFieldHiddenFromSearch(tableName: string, fieldName: string): boolean {
  const key = `${tableName}.${fieldName}`;
  return getHiddenSearchFields().includes(key);
}

/**
 * BooleanSearch Component
 */
export class BooleanSearch implements IComponent {
  private container: HTMLElement;
  private options: BooleanSearchOptions;
  private tree: BooleanSearchGroup;
  private ruleIdCounter = 0;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private boundClickHandler: ((e: Event) => void) | null = null;
  private boundChangeHandler: ((e: Event) => void) | null = null;
  private boundInputHandler: ((e: Event) => void) | null = null;
  private selectedGroupPath: string = ''; // Currently selected group for adding fields
  
  // Loading state
  private isSearching = false;
  private searchAbortController: AbortController | null = null;
  
  // Fields built from StandingData (TriSysWebAPI mode) or provided directly (Custom mode)
  private builtFields: BooleanSearchField[] = [];
  private genericFields: BooleanSearchField[] = [];
  private crossEntityFieldValues: Record<string, { value: string; label: string }[]> = {};
  
  // DatePicker instances for date fields (keyed by rule path)
  private datePickerInstances: Map<string, DatePicker> = new Map();
  
  // TaskTypeDropdown instances for Task fields (keyed by rule path)
  private taskTypeDropdownInstances: Map<string, TaskTypeDropdown> = new Map();
  
  // CurrencyAmountPeriod instances for currency fields (keyed by rule path)
  private currencyAmountPeriodInstances: Map<string, CurrencyAmountPeriod> = new Map();

  constructor(container: HTMLElement, options: BooleanSearchOptions) {
    this.container = container;
    this.options = {
      mode: 'TriSysWebAPI',  // Default mode
      compact: false,
      showSearchButton: true,
      showClearButton: true,
      autoSearch: false,
      debounceDelay: 300,
      ...options
    };
    
    // Initialize with provided tree or empty AND group
    this.tree = options.initialTree ?? {
      condition: 'AND',
      rules: []
    };
  }

  async initialize(): Promise<void> {
    // Build fields based on mode
    if (this.options.mode === 'TriSysWebAPI') {
      if (!this.options.standingData) {
        logger.error('TriSysWebAPI mode requires standingData option');
        throw new Error('BooleanSearch: standingData is required for TriSysWebAPI mode');
      }
      this.buildFieldsFromStandingData();
    } else {
      // Custom mode - use fields provided directly
      this.builtFields = this.options.fields || [];
      this.genericFields = [];
      this.crossEntityFieldValues = {};
    }
    
    this.render();
    this.bindEvents();
    await this.initializeDatePickers();
    await this.initializeTaskTypeDropdowns();
    await this.initializeCurrencyAmountPeriodComponents();
    logger.debug('BooleanSearch initialized', { 
      mode: this.options.mode,
      entityName: this.options.entityName,
      fieldCount: this.builtFields.length,
      genericFieldCount: this.genericFields.length
    });
  }

  /**
   * Build fields from StandingData cache (TriSysWebAPI mode)
   * This encapsulates all the field building logic previously in the sample form.
   */
  private buildFieldsFromStandingData(): void {
    const standingData = this.options.standingData!;
    const entityName = this.options.entityName;
    
    // Filter fields for this entity (including ConfigFields table)
    const configTableName = `${entityName}ConfigFields`;
    const entityFields = standingData.FieldDescriptions?.filter(fd => {
      const tableName = fd.TableName?.toLowerCase() || '';
      return tableName === entityName.toLowerCase() || 
             tableName === configTableName.toLowerCase();
    }) || [];
    
    logger.debug('Building fields from StandingData', { 
      entityName, 
      fieldCount: entityFields.length 
    });
    
    // Build BooleanSearchField array with lookup values
    // IMPORTANT: Use Apex format {TableName}_{FieldName} for field IDs
    // This ensures compatibility with saved searches which use this format
    // e.g., "Name" field in "Company" table becomes "Company_Name"
    this.builtFields = entityFields.map(fd => {
      // Build Apex-compatible field ID: TableName_FieldName
      const rawFieldId = `${fd.TableName}_${fd.TableFieldName}`;
      
      // Apply Apex field ID exceptions - matches Apex's FieldDescriptionException function
      // The API uses legacy field naming that doesn't match database field names
      const fieldId = this.applyApexFieldIdExceptions(rawFieldId);
      
      // Skip fields that should be excluded (null returned from exception handler)
      if (fieldId === null) {
        return null as any; // Will be filtered out below
      }
      
      const field: BooleanSearchField = {
        id: fieldId,
        label: (fd.FieldLabel || fd.TableFieldName).replace(/&/g, ''),  // Remove & characters
        type: this.mapFieldTypeId(fd.FieldTypeId),
        fieldTypeId: fd.FieldTypeId, // Store original FieldTypeId for special handling
        input: 'text',
        // Mark CurrencyAmountPeriod fields (FieldTypeId 39) for special composite widget
        isCurrencyAmountPeriodField: fd.FieldTypeId === 39
      };
      
      // Handle special lookups that don't come from SkillCategories
      
      // 1. Priority field for Contact entity uses ContactPriorities
      if (fd.TableFieldName === 'Priority' && fd.TableName === 'Contact' && standingData.ContactPriorities) {
        field.values = standingData.ContactPriorities.map(p => ({ 
          value: p.ContactPriority, 
          label: p.ContactPriority 
        }));
        field.input = 'select';
        field.default_operator = 'in';
        return field;
      }
      
      // 2. Type/EntityType field uses EntityTypes filtered by current entity
      // IMPORTANT: Apex uses "{EntityName}Type" format (e.g., "CompanyType" not "Company_Type")
      // Type must be 'integer' and values must be EntityTypeId numbers
      if ((fd.TableFieldName === 'Type' || fd.TableFieldName === 'EntityTypeId') && standingData.EntityTypes) {
        const entityTypes = standingData.EntityTypes.filter(et => et.EntityName === fd.TableName);
        if (entityTypes.length > 0) {
          // Override field ID to match Apex format: {EntityName}Type (no underscore)
          field.id = `${fd.TableName}Type`;
          field.type = 'integer';  // Apex sends type:"integer"
          field.values = entityTypes.map(et => ({ 
            value: et.EntityTypeId,  // Keep as number - Apex sends [27] not ["27"]
            label: et.Name 
          }));
          field.input = 'select';
          field.default_operator = 'in';
          return field;
        }
      }
      
      // 3. IndustryId field for Company entity uses Industries
      // IMPORTANT: Apex uses "Company_Industry" as the field ID (not Company_IndustryId)
      // Type must be 'integer' and values must be numeric (not strings)
      if (fd.TableFieldName === 'IndustryId' && fd.TableName === 'Company' && standingData.Industries) {
        // Override the field ID and type to match Apex format
        field.id = 'Company_Industry';
        field.type = 'integer';  // Apex sends type:"integer"
        field.values = standingData.Industries.map(ind => ({ 
          value: ind.IndustryId,  // Keep as number, not string - Apex sends [162] not ["162"]
          label: ind.Name         // Display Name in dropdown
        }));
        field.input = 'select';
        field.default_operator = 'in';
        return field;
      }
      
      // Find lookup values from Skills for this field
      // Try matching by FieldId first
      let skillCategory = standingData.SkillCategories?.find(sc => sc.FieldId === fd.FieldId);
      
      // If no match by FieldId, try matching by Category name = TableFieldName
      if (!skillCategory) {
        skillCategory = standingData.SkillCategories?.find(sc => 
          sc.Category === fd.TableFieldName || 
          sc.Category?.toLowerCase() === fd.TableFieldName?.toLowerCase()
        );
      }
      
      if (skillCategory) {
        const skills = standingData.Skills?.filter(s => s.SkillCategoryId === skillCategory.SkillCategoryId) || [];
        if (skills.length > 0) {
          // IMPORTANT: Apex uses SkillId (integer) as values, not Skill names
          // This ensures saved searches load correctly with proper label display
          field.type = 'integer';
          field.values = skills.map(s => ({ 
            value: s.SkillId,  // Use SkillId number for Apex compatibility
            label: s.Skill     // Display name
          }));
          field.input = 'select';
          field.default_operator = 'in';
        }
      }
      
      return field;
    }).filter(f => f !== null);  // Filter out excluded fields
    
    // IMPORTANT: Ensure essential core fields exist for each entity
    // These are core table fields that may not be in FieldDescriptions but are essential for searches
    // The API documentation confirms these fields exist and are searchable
    this.ensureEssentialFields(entityName);
    
    // Sort by label, with common fields first
    const priorityFields = ['Name', 'CompanyName', 'Forenames', 'Surname', 'Status', 'Type', 'Source', 'Priority'];
    this.builtFields.sort((a, b) => {
      const aIdx = priorityFields.findIndex(p => a.id.includes(p));
      const bIdx = priorityFields.findIndex(p => b.id.includes(p));
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.label.localeCompare(b.label);
    });
    
    // Load generic skills (SkillCategories with FieldId: 0)
    // These are skills that can be assigned to ANY entity (e.g., GDPR, Languages Spoken)
    // IMPORTANT: Apex format uses "{EntityName}Skill_{Category}" (e.g., "CompanySkill_Languages Spoken")
    // Type must be 'integer' and values must be SkillId numbers
    this.genericFields = [];
    const genericCategories = standingData.SkillCategories?.filter(sc => sc.FieldId === 0) || [];
    
    for (const category of genericCategories) {
      const skills = standingData.Skills?.filter(s => s.SkillCategoryId === category.SkillCategoryId) || [];
      if (skills.length > 0) {
        this.genericFields.push({
          id: category.Category,  // Base ID - will be prefixed in getVisibleFields()
          label: category.Category,
          type: 'integer',  // Apex sends type:"integer" for skills
          input: 'select',
          values: skills.map(s => ({ 
            value: s.SkillId,  // Use SkillId number, not Skill name
            label: s.Skill     // Display name
          })),
          default_operator: 'in',
          isGenericSkill: true  // Flag to indicate this needs entity prefix
        });
      }
    }
    
    // Build cross-entity field values (for fields from junction tables)
    this.crossEntityFieldValues = {};
    
    // User field - values from Users cache
    if (standingData.Users && standingData.Users.length > 0) {
      this.crossEntityFieldValues['User'] = standingData.Users
        .filter(u => !u.Locked)
        .map(u => ({
          value: String(u.UserId),
          label: u.FullName
        }));
    }
    
    // Task field - values from TaskTypes cache
    // NOTE: TaskTypes must be added to StandingDataResponse or fetched separately
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyStandingData = standingData as any;
    if (anyStandingData.TaskTypes && anyStandingData.TaskTypes.length > 0) {
      this.crossEntityFieldValues['Task'] = anyStandingData.TaskTypes.map((t: { TaskTypeId: number; Name: string }) => ({
        value: t.Name,  // Use Name as value for "TaskTypeName,startDate,endDate" format
        label: t.Name
      }));
    }
    
    logger.debug('Built fields from StandingData', {
      builtFieldCount: this.builtFields.length,
      genericFieldCount: this.genericFields.length,
      crossEntityFieldCount: Object.keys(this.crossEntityFieldValues).length
    });
  }
  
  /**
   * Ensure essential core entity fields exist in builtFields.
   * Some core table fields (like "Name" for Company) are not in FieldDescriptions
   * but are essential for searches and exist in the API.
   */
  private ensureEssentialFields(entityName: string): void {
    const essentialFieldsByEntity: Record<string, BooleanSearchField[]> = {
      'Company': [
        { id: 'Company_Name', label: 'Name', type: 'string', input: 'text' }
      ],
      // Add other entities as needed
      // 'Contact': [...],  // Contact already has all fields from StandingData
    };
    
    const essentialFields = essentialFieldsByEntity[entityName] || [];
    
    for (const essentialField of essentialFields) {
      const exists = this.builtFields.some(f => f.id.toLowerCase() === essentialField.id.toLowerCase());
      if (!exists) {
        // Add at the beginning for high visibility
        this.builtFields.unshift(essentialField);
        logger.debug('Added essential field missing from StandingData', { 
          entityName, 
          fieldId: essentialField.id 
        });
      }
    }
  }
  
  /**
   * Map FieldTypeId to BooleanSearch field type
   * 
   * @see Apex FieldTypes for the full list of FieldTypeIds
   */
  private mapFieldTypeId(typeId: number): FieldType {
    switch (typeId) {
      case 2: return 'integer';
      case 3: return 'double';
      case 4:
      case 5: return 'date';
      case 6: return 'boolean';
      case 38: return 'double';  // CurrencyAmount (currency + numeric amount)
      case 39: return 'double';  // CurrencyAmountPeriod (currency + numeric amount + period)
      default: return 'string';
    }
  }

  /**
   * Apply Apex field ID exceptions - maps database field names to Apex legacy field names.
   * The TriSys API uses legacy field naming that doesn't always match current database field names.
   * This matches Apex's FieldDescriptionException function in ctrlSearchCriteria.js
   * 
   * @param fieldId - The database field ID in format TableName_FieldName
   * @returns The Apex-compatible field ID, or null if the field should be excluded
   */
  private applyApexFieldIdExceptions(fieldId: string): string | null {
    switch (fieldId) {
      // Contact table - Apex uses legacy field names
      case 'Contact_Forenames':
        return 'Contact_Christian';  // Legacy: "Christian" was the original field name
      
      case 'Contact_Age':
        return null;  // Age field should not be searchable
      
      default:
        // Filter out garbage data from legacy database
        if (fieldId.includes('\n')) {
          return null;
        }
        return fieldId;
    }
  }

  destroy(): void {
    this.destroyDatePickers();
    this.destroyTaskTypeDropdowns();
    this.destroyCurrencyAmountPeriodComponents();
    this.unbindEvents();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.container.innerHTML = '';
  }

  /**
   * Unbind event handlers (prevent duplicate listeners)
   */
  private unbindEvents(): void {
    if (this.boundClickHandler) {
      this.container.removeEventListener('click', this.boundClickHandler);
      this.boundClickHandler = null;
    }
    if (this.boundChangeHandler) {
      this.container.removeEventListener('change', this.boundChangeHandler);
      this.boundChangeHandler = null;
    }
    if (this.boundInputHandler) {
      this.container.removeEventListener('input', this.boundInputHandler);
      this.boundInputHandler = null;
    }
  }

  /**
   * Re-render the component and reinitialize field components
   * Use this method instead of calling render() + bindEvents() + initialize*() separately
   */
  private async reRender(): Promise<void> {
    this.render();
    this.bindEvents();
    await this.initializeDatePickers();
    await this.initializeTaskTypeDropdowns();
    await this.initializeCurrencyAmountPeriodComponents();
  }

  /**
   * Synchronous re-render that queues field component initialization
   * Use for event handlers that cannot be async
   */
  private reRenderSync(): void {
    this.render();
    this.bindEvents();
    // Fire-and-forget component initialization
    Promise.all([
      this.initializeDatePickers(),
      this.initializeTaskTypeDropdowns(),
      this.initializeCurrencyAmountPeriodComponents()
    ]).catch(err => {
      logger.error('Failed to initialize field components', { error: err });
    });
  }

  // ===== Public API =====

  /**
   * Get the current search tree as JSON
   */
  getSearchTree(): SearchTreeJson | null {
    if (this.tree.rules.length === 0) {
      return null;
    }
    
    return {
      EntityName: this.options.entityName,
      RecordId: 0,
      Tree: this.cleanTree(this.tree)
    };
  }

  /**
   * Get the search tree JSON string for API
   */
  getSearchTreeJson(): string {
    const tree = this.getSearchTree();
    return tree ? JSON.stringify(tree) : '';
  }

  /**
   * Set the search tree (restore saved search)
   */
  async setTree(tree: BooleanSearchGroup): Promise<void> {
    this.tree = tree;
    await this.reRender();
    this.notifyChange();
  }

  /**
   * Load a complete SearchTreeJson structure.
   * This is the inverse of getSearchTree() - validates and applies the full tree.
   * 
   * @param json - The SearchTreeJson object or JSON string to load
   * @returns true if loaded successfully, false if validation failed
   * @throws Error if JSON parsing fails
   * 
   * @example
   * // Load from object
   * await booleanSearch.loadTree({
   *   EntityName: "Contact",
   *   RecordId: 0,
   *   Tree: { condition: 'AND', rules: [...] }
   * });
   * 
   * @example
   * // Load from JSON string
   * await booleanSearch.loadTree('{"EntityName":"Contact","RecordId":0,"Tree":{...}}');
   */
  async loadTree(json: SearchTreeJson | string): Promise<boolean> {
    let treeData: SearchTreeJson;
    
    // Parse if string
    if (typeof json === 'string') {
      try {
        treeData = JSON.parse(json);
      } catch (error) {
        logger.error('Failed to parse tree JSON', { error });
        throw new Error('Invalid JSON format');
      }
    } else {
      treeData = json;
    }
    
    // Validate structure
    const validationErrors = this.validateTreeStructure(treeData);
    if (validationErrors.length > 0) {
      logger.warn('Tree validation failed', { errors: validationErrors });
      return false;
    }
    
    // Optional: warn if EntityName doesn't match current entity
    if (treeData.EntityName && treeData.EntityName !== this.options.entityName) {
      logger.warn('Loaded tree EntityName differs from current entity', {
        treeEntity: treeData.EntityName,
        currentEntity: this.options.entityName
      });
    }
    
    // Apply the tree
    this.tree = treeData.Tree;
    await this.reRender();
    this.notifyChange();
    
    logger.debug('Tree loaded successfully', { 
      ruleCount: this.countRules(this.tree),
      entityName: treeData.EntityName 
    });
    
    return true;
  }

  /**
   * Validate a SearchTreeJson structure before loading.
   * Returns an array of validation error messages, empty if valid.
   */
  validateTreeStructure(treeData: unknown): string[] {
    const errors: string[] = [];
    
    if (!treeData || typeof treeData !== 'object') {
      errors.push('Tree data must be an object');
      return errors;
    }
    
    const data = treeData as Record<string, unknown>;
    
    // Check required properties
    if (!data.Tree || typeof data.Tree !== 'object') {
      errors.push('Tree property is required and must be an object');
      return errors;
    }
    
    // Validate the Tree structure recursively
    this.validateGroup(data.Tree as Record<string, unknown>, 'Tree', errors);
    
    return errors;
  }

  /**
   * Recursively validate a group structure
   */
  private validateGroup(group: Record<string, unknown>, path: string, errors: string[]): void {
    // Check condition
    if (!group.condition || (group.condition !== 'AND' && group.condition !== 'OR')) {
      errors.push(`${path}.condition must be 'AND' or 'OR'`);
    }
    
    // Check rules array
    if (!Array.isArray(group.rules)) {
      errors.push(`${path}.rules must be an array`);
      return;
    }
    
    // Validate each rule
    group.rules.forEach((item: unknown, index: number) => {
      const rulePath = `${path}.rules[${index}]`;
      
      if (!item || typeof item !== 'object') {
        errors.push(`${rulePath} must be an object`);
        return;
      }
      
      const rule = item as Record<string, unknown>;
      
      // Check if it's a group (has condition) or a rule (has field)
      if ('condition' in rule) {
        // It's a nested group
        this.validateGroup(rule, rulePath, errors);
      } else if ('field' in rule) {
        // It's a rule - validate rule properties
        if (typeof rule.field !== 'string' || !rule.field) {
          errors.push(`${rulePath}.field must be a non-empty string`);
        }
        if (!rule.operator) {
          errors.push(`${rulePath}.operator is required`);
        }
        // id is optional but useful for tracking
        // type and input are optional - will use defaults from field definition
      } else {
        errors.push(`${rulePath} must have either 'condition' (group) or 'field' (rule)`);
      }
    });
  }

  /**
   * Count total rules in a tree (for debugging/logging)
   */
  private countRules(group: BooleanSearchGroup): number {
    let count = 0;
    for (const item of group.rules) {
      if ('condition' in item) {
        count += this.countRules(item);
      } else {
        count++;
      }
    }
    return count;
  }

  /**
   * Get the current tree as a formatted JSON string (for display/editing)
   */
  getFormattedJson(): string {
    const tree = this.getSearchTree();
    return tree ? JSON.stringify(tree, null, 2) : '{\n  "EntityName": "' + this.options.entityName + '",\n  "RecordId": 0,\n  "Tree": {\n    "condition": "AND",\n    "rules": []\n  }\n}';
  }

  /**
   * Clear all rules
   */
  async clear(): Promise<void> {
    this.tree = { condition: 'AND', rules: [] };
    await this.reRender();
    this.notifyChange();
  }

  /**
   * Check if search has any rules
   */
  hasRules(): boolean {
    return this.tree.rules.length > 0;
  }

  // ===== Private Methods =====

  /**
   * Clean tree by removing internal metadata (like _ruleId)
   */
  private cleanTree(group: BooleanSearchGroup): BooleanSearchGroup {
    return {
      condition: group.condition,
      rules: group.rules.map(rule => {
        if ('condition' in rule) {
          return this.cleanTree(rule);
        }
        const { id, field, type, input, operator, value } = rule;
        return { id, field, type, input, operator, value };
      })
    };
  }

  /**
   * Generate unique rule ID
   */
  private generateRuleId(): string {
    return `rule_${++this.ruleIdCounter}`;
  }

  /**
   * Get operators for a field type
   * @deprecated Use getOperatorsForField() for full field-type-aware operator selection
   */
  private getOperatorsForType(type: FieldType, hasValues: boolean): BooleanOperator[] {
    return this.getOperatorsForField({ id: '', label: '', type }, hasValues);
  }
  
  /**
   * Get operators for a field, taking into account the field's type and special cases
   * 
   * Special handling:
   * - Boolean/Yes-No (fieldTypeId: 6): Only 'equal' operator
   * - Date (fieldTypeId: 7 or type: 'date'): 'before', 'after', 'on', 'not_on', 'between'
   * - Integer/Double (fieldTypeId: 2,3,4 or type: 'integer'/'double'): Numeric comparison operators
   * - Task field (isTaskField: true): 'in', 'not_in' only
   * - Address Radius field (isAddressRadiusField: true): 'less', 'less_or_equal', 'greater', 'greater_or_equal' only
   */
  private getOperatorsForField(field: Partial<BooleanSearchField>, hasValues: boolean): BooleanOperator[] {
    const type = field.type || 'string';
    const fieldTypeId = field.fieldTypeId;
    
    // Special case: Task field - only in/not_in
    if (field.isTaskField) {
      return ['in', 'not_in'];
    }
    
    // Special case: Address Radius field - comparison operators only
    if (field.isAddressRadiusField) {
      return ['less', 'less_or_equal', 'greater', 'greater_or_equal'];
    }
    
    // Special case: Boolean/Yes-No field (FieldTypeId: 6)
    if (type === 'boolean' || fieldTypeId === 6) {
      return ['equal'];
    }
    
    // Special case: Date field (FieldTypeId: 7 or type is date/datetime)
    if (type === 'date' || type === 'datetime' || fieldTypeId === 7) {
      // Use user-friendly date operators instead of less/greater
      return ['before', 'after', 'on', 'not_on', 'between', 'not_between', 'is_null', 'is_not_null'];
    }
    
    // Special case: Numeric fields (FieldTypeId: 2, 3, 4)
    if (type === 'integer' || type === 'double' || fieldTypeId === 2 || fieldTypeId === 3 || fieldTypeId === 4) {
      return ['equal', 'not_equal', 'greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'not_between', 'is_null', 'is_not_null'];
    }
    
    // Standard behavior for string and other types
    const ops = Object.entries(OPERATORS)
      .filter(([_, def]) => def.types.includes(type))
      .map(([op]) => op as BooleanOperator);
    
    // If field has predefined values, prioritize 'in' operator
    if (hasValues) {
      const inIdx = ops.indexOf('in');
      if (inIdx > 0) {
        ops.splice(inIdx, 1);
        ops.unshift('in');
      }
    } else if (type === 'string') {
      // For text fields without lookup values, default to 'contains'
      const containsIdx = ops.indexOf('contains');
      if (containsIdx > 0) {
        ops.splice(containsIdx, 1);
        ops.unshift('contains');
      }
    }
    
    return ops;
  }

  /**
   * Get field definition by ID
   * Searches in visible fields (which includes base fields, cross-entity, and generic fields)
   */
  private getField(fieldId: string): BooleanSearchField | undefined {
    // Use getVisibleFields() to get the properly merged field list
    // This ensures we get the correct field definition (especially for generic skills)
    return this.getVisibleFields().find(f => f.id === fieldId);
  }

  /**
   * Get visible fields (filtered and sorted)
   * 
   * Filtering logic:
   * 1. Start with FieldDescriptions from Standing Data (this.options.fields)
   * 2. For LOOKUP fields (have values): Include ALL except those in IgnoredLookupTableFieldNames
   * 3. For NON-LOOKUP fields: Only include if in AllowedTableFieldNames
   * 4. Exclude fields in getHiddenSearchFields()
   * 5. Merge in cross-entity fields (these are always included)
   * 6. Sort alphabetically by label
   * 
   * @see specifications/Components/search-criteria-entity-fields.json
   */
  private getVisibleFields(): BooleanSearchField[] {
    const entityName = this.options.entityName;
    const hiddenFields = getHiddenSearchFields();
    const entityConfig = getEntityFieldConfig(entityName);
    
    // Get base fields (built from StandingData or provided directly), applying all filters
    const baseFields = this.builtFields.filter(f => {
      // 1. Check if field is in hidden list (applies to all fields)
      if (hiddenFields.includes(`${entityName}.${f.id}`)) {
        return false;
      }
      
      // 2. Determine if this is a lookup/skill field (has predefined values)
      const isLookupField = f.values && f.values.length > 0;
      
      // Extract just the TableFieldName from the id (format: TableName_FieldName)
      const tableFieldName = f.id.includes('_') ? f.id.split('_').pop()! : f.id;
      
      if (isLookupField) {
        // LOOKUP FIELDS: Include all EXCEPT those in IgnoredLookupTableFieldNames
        if (entityConfig && entityConfig.IgnoredLookupTableFieldNames.includes(tableFieldName)) {
          return false;
        }
        // Include this lookup field
        return true;
      } else {
        // NON-LOOKUP FIELDS: Only include if in AllowedTableFieldNames
        if (entityConfig && !entityConfig.AllowedTableFieldNames.includes(tableFieldName)) {
          return false;
        }
        return true;
      }
    });
    
    // Get cross-entity fields for this entity (always included)
    const crossEntityFields = getCrossEntityFieldsForEntity(entityName);
    
    // Merge fields - use a Map to deduplicate by ID
    const fieldMap = new Map<string, BooleanSearchField>();
    
    // Add base fields first
    for (const field of baseFields) {
      fieldMap.set(field.id, field);
    }
    
    // Add cross-entity fields (these have unique IDs like 'AgreeToMarketingCommunications')
    for (const field of crossEntityFields) {
      if (!fieldMap.has(field.id)) {
        // Check if we have values for this cross-entity field
        const crossEntityValues = this.crossEntityFieldValues[field.id];
        if (crossEntityValues && crossEntityValues.length > 0) {
          fieldMap.set(field.id, {
            ...field,
            values: crossEntityValues,
            input: 'select',
            default_operator: 'in'
          });
        } else {
          fieldMap.set(field.id, field);
        }
      }
    }
    
    // Add generic fields (skills with FieldId: 0, like GDPR, Languages Spoken)
    // These OVERRIDE existing fields because they have the correct skill values
    // A FieldDescription may exist with the same name but linked to wrong/no skills
    // IMPORTANT: Apex uses "{EntityName}Skill_{Category}" format (e.g., "CompanySkill_Languages Spoken")
    for (const field of this.genericFields) {
      const prefixedId = `${this.options.entityName}Skill_${field.id}`;
      fieldMap.set(prefixedId, {
        ...field,
        id: prefixedId  // Override ID with entity prefix
      });
    }
    
    // Sort alphabetically by label
    return Array.from(fieldMap.values())
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Render the component
   */
  private render(): void {
    const compact = this.options.compact ? 'p-3' : 'p-4';
    const selectedIndicator = this.selectedGroupPath ? 
      `<span class="badge badge-primary badge-sm ml-1">→ ${this.selectedGroupPath || 'root'}</span>` : '';
    
    this.container.innerHTML = `
      <div class="boolean-search bg-base-200 rounded-lg ${compact}">
        <div class="flex items-center justify-between mb-5">
          <div class="flex items-center gap-2">
            <i class="fas fa-filter text-primary"></i>
            <span class="font-semibold">Boolean Search</span>
          </div>
          <div class="flex items-center gap-2">
            <button type="button" class="btn btn-sm btn-outline btn-primary" data-action="add-field" title="Add Field to ${this.selectedGroupPath || 'root'} group">
              <i class="fas fa-plus mr-1"></i> Add Field${selectedIndicator}
            </button>
            <button type="button" class="btn btn-sm btn-outline" data-action="add-group" title="Add Group to ${this.selectedGroupPath || 'root'} group">
              <i class="fas fa-layer-group mr-1"></i> Add Group
            </button>
          </div>
        </div>
        
        <div class="boolean-search-tree" data-group-path="">
          ${this.renderGroup(this.tree, '')}
        </div>
        
        ${this.options.showSearchButton || this.options.showClearButton ? `
          <div class="flex justify-start gap-2 mt-5">
            ${this.options.showSearchButton ? `
              <button type="button" class="btn btn-primary btn-sm" data-action="search" data-search-button>
                <i class="fas fa-search mr-1" data-search-icon></i>
                <span class="loading loading-spinner loading-xs mr-1 hidden" data-search-spinner></span>
                <span data-search-text>Search/Lookup</span>
              </button>
            ` : ''}
            ${this.options.showClearButton ? `
              <button type="button" class="btn btn-error btn-sm" data-action="clear" data-clear-button>
                <i class="fas fa-times mr-1"></i> <span data-clear-text>Clear Search</span>
              </button>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Render a group (AND/OR container)
   */
  private renderGroup(group: BooleanSearchGroup, path: string): string {
    const isRoot = path === '';
    const isSelected = this.selectedGroupPath === path;
    const highlightClass = isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-base-200' : '';
    const groupClass = isRoot ? '' : 'ml-6 mt-2 pl-4 border-l-2 border-base-300';
    
    const rulesHtml = group.rules.map((rule, index) => {
      const rulePath = path ? `${path}.${index}` : `${index}`;
      if ('condition' in rule) {
        return this.renderGroup(rule, rulePath);
      }
      return this.renderRule(rule, rulePath);
    }).join('');

    return `
      <div class="boolean-group ${groupClass} ${highlightClass} rounded-lg p-2 -m-2 transition-all" data-path="${path}">
        <div class="flex items-center gap-2 mb-2">
          <button type="button" class="btn btn-xs ${isSelected ? 'btn-primary' : 'btn-ghost'}" 
                  data-action="select-group" data-path="${path}" title="Select this group for adding fields">
            <i class="fas fa-crosshairs"></i>
          </button>
          <div class="join">
            <button type="button" class="btn btn-xs join-item ${group.condition === 'AND' ? 'btn-primary' : 'btn-outline'}" 
                    data-action="set-condition" data-condition="AND" data-path="${path}">
              AND
            </button>
            <button type="button" class="btn btn-xs join-item ${group.condition === 'OR' ? 'btn-secondary' : 'btn-outline'}" 
                    data-action="set-condition" data-condition="OR" data-path="${path}">
              OR
            </button>
          </div>
          ${!isRoot ? `
            <button type="button" class="btn btn-xs btn-ghost btn-error" data-action="delete-group" data-path="${path}">
              <i class="fas fa-trash"></i>
            </button>
          ` : ''}
        </div>
        <div class="boolean-rules space-y-2">
          ${rulesHtml || '<div class="text-base-content/50 text-sm italic py-2">No criteria added. Click "Add field" to start.</div>'}
        </div>
      </div>
    `;
  }

  /**
   * Render a single rule
   */
  private renderRule(rule: BooleanSearchRule, path: string): string {
    const field = this.getField(rule.field);
    const operators = field?.operators || this.getOperatorsForField(field || { type: rule.type }, !!(field?.values?.length));
    const operatorDef = OPERATORS[rule.operator];
    
    // Get visible fields (already filtered and sorted)
    const visibleFields = this.getVisibleFields();
    
    return `
      <div class="boolean-rule flex items-start gap-2 bg-base-100 rounded-lg p-3" data-path="${path}">
        <!-- Field selector -->
        <select class="select select-sm select-bordered w-72" data-action="change-field" data-path="${path}">
          ${visibleFields.map(f => `
            <option value="${f.id}" ${f.id === rule.field ? 'selected' : ''}>${f.label.replace(/&/g, '')}</option>
          `).join('')}
        </select>
        
        <!-- Operator selector -->
        <select class="select select-sm select-bordered w-72" data-action="change-operator" data-path="${path}">
          ${operators.map(op => `
            <option value="${op}" ${op === rule.operator ? 'selected' : ''}>${OPERATORS[op].label}</option>
          `).join('')}
        </select>
        
        <!-- Value input(s) -->
        <div class="flex-1 min-w-0">
          ${this.renderValueInput(rule, path, field, operatorDef.nb_inputs)}
        </div>
        
        <!-- Delete button -->
        <button type="button" class="btn btn-sm btn-error btn-outline" data-action="delete-rule" data-path="${path}">
          <i class="fas fa-trash-alt"></i> Delete
        </button>
      </div>
    `;
  }

  /**
   * Render value input based on field type and operator
   */
  private renderValueInput(
    rule: BooleanSearchRule, 
    path: string, 
    field: BooleanSearchField | undefined,
    nbInputs: number
  ): string {
    if (nbInputs === 0) {
      return '<span class="text-base-content/50 text-sm italic">No value needed</span>';
    }

    // Special case: Task field - composite widget with TaskType + date range
    if (field?.isTaskField) {
      return this.renderTaskFieldInput(rule, path, field);
    }

    // Special case: Address Radius field - composite widget with distance + postcode
    if (field?.isAddressRadiusField) {
      return this.renderAddressRadiusInput(rule, path);
    }

    // Special case: CurrencyAmountPeriod field - composite widget with currency + amount + period
    if (field?.isCurrencyAmountPeriodField) {
      return this.renderCurrencyAmountPeriodInput(rule, path);
    }

    // Special case: Boolean/Yes-No field (checkbox only)
    if (rule.type === 'boolean' || field?.fieldTypeId === 6) {
      const isChecked = rule.value === true || rule.value === 'true' || rule.value === '1';
      return `
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" class="checkbox checkbox-primary" 
                 ${isChecked ? 'checked' : ''} 
                 data-action="change-boolean-value" data-path="${path}">
          <span class="label-text">${isChecked ? 'Yes' : 'No'}</span>
        </label>
      `;
    }

    // Multi-value with chips (for 'in' and 'not_in' operators with predefined values)
    if ((rule.operator === 'in' || rule.operator === 'not_in') && field?.values?.length) {
      const selectedValues = Array.isArray(rule.value) ? rule.value : [];
      return `
        <div class="flex flex-wrap items-center gap-2">
          ${selectedValues.map((v: string | number) => {
            // Compare as strings to handle both numeric and string values
            const valLabel = field.values?.find(fv => String(fv.value) === String(v))?.label || String(v);
            return `
              <span class="badge badge-primary gap-1">
                ${valLabel}
                <button type="button" class="btn btn-ghost btn-xs p-0" data-action="remove-chip" data-path="${path}" data-value="${v}">
                  <i class="fas fa-times text-xs"></i>
                </button>
              </span>
            `;
          }).join('')}
          <select class="select select-sm select-bordered flex-1 min-w-32" data-action="add-chip" data-path="${path}">
            <option value="">Select...</option>
            ${field.values.filter(v => !selectedValues.some((sv: string | number) => String(sv) === String(v.value))).map(v => `
              <option value="${v.value}">${v.label}</option>
            `).join('')}
          </select>
          <button type="button" class="btn btn-xs btn-ghost" data-action="clear-chips" data-path="${path}" title="Clear all">
            <i class="fas fa-times"></i>
          </button>
        </div>
      `;
    }

    // Select dropdown for fields with predefined values
    if (field?.values?.length) {
      return `
        <select class="select select-sm select-bordered w-full" data-action="change-value" data-path="${path}">
          <option value="">Select...</option>
          ${field.values.map(v => `
            <option value="${v.value}" ${v.value === rule.value ? 'selected' : ''}>${v.label}</option>
          `).join('')}
        </select>
      `;
    }

    // Between operator needs two inputs
    if (nbInputs === 2) {
      const values = Array.isArray(rule.value) ? rule.value : ['', ''];
      
      // Special case: Date between - use DatePicker components
      if (rule.type === 'date' || rule.type === 'datetime' || field?.fieldTypeId === 7) {
        return `
          <div class="flex items-center gap-2">
            <div class="datepicker-container flex-1" data-datepicker-path="${path}" data-datepicker-index="0" data-datepicker-value="${values[0] || ''}"></div>
            <span class="text-base-content/50 font-medium">and</span>
            <div class="datepicker-container flex-1" data-datepicker-path="${path}" data-datepicker-index="1" data-datepicker-value="${values[1] || ''}"></div>
          </div>
        `;
      }
      
      // Numeric between
      const inputType = this.getHtmlInputType(rule.type);
      const step = rule.type === 'double' ? '0.01' : '1';
      return `
        <div class="flex items-center gap-2">
          <input type="${inputType}" class="input input-sm input-bordered flex-1" step="${step}"
                 value="${values[0] || ''}" data-action="change-value" data-path="${path}" data-index="0" placeholder="From">
          <span class="text-base-content/50 font-medium">and</span>
          <input type="${inputType}" class="input input-sm input-bordered flex-1" step="${step}"
                 value="${values[1] || ''}" data-action="change-value" data-path="${path}" data-index="1" placeholder="To">
        </div>
      `;
    }

    // Date field - use DatePicker component (initialized after render)
    if (rule.type === 'date' || rule.type === 'datetime' || field?.fieldTypeId === 7) {
      const value = typeof rule.value === 'string' ? rule.value : '';
      // Render a container for the DatePicker component
      // The actual DatePicker will be initialized in initializeDatePickers()
      return `
        <div class="datepicker-container" data-datepicker-path="${path}" data-datepicker-value="${value}"></div>
      `;
    }

    // Numeric field - number input with step buttons
    if (rule.type === 'integer' || rule.type === 'double' || 
        field?.fieldTypeId === 2 || field?.fieldTypeId === 3 || field?.fieldTypeId === 4) {
      const value = typeof rule.value === 'string' || typeof rule.value === 'number' ? rule.value : '';
      const step = rule.type === 'double' ? '0.01' : '1';
      return `
        <input type="number" class="input input-sm input-bordered w-full" 
               value="${value}" step="${step}" data-action="change-value" data-path="${path}" 
               placeholder="Enter number...">
      `;
    }

    // Standard single input (string fields)
    const inputType = this.getHtmlInputType(rule.type);
    const value = typeof rule.value === 'string' || typeof rule.value === 'number' ? rule.value : '';
    return `
      <input type="${inputType}" class="input input-sm input-bordered w-full" 
             value="${value}" data-action="change-value" data-path="${path}" 
             placeholder="Enter value...">
    `;
  }
  
  /**
   * Render Task field composite widget
   * Shows TaskType selector (with icons) + start/end date pickers
   * Value format: "TaskTypeName,startDate,endDate"
   * 
   * In TriSysWebAPI mode, uses TaskTypeDropdown component which auto-populates from StandingData.
   * In Custom mode, falls back to a simple select with field.values.
   */
  private renderTaskFieldInput(
    rule: BooleanSearchRule, 
    path: string, 
    field: BooleanSearchField
  ): string {
    // Parse current value: "TaskTypeName,startDate,endDate"
    const valueStr = typeof rule.value === 'string' ? rule.value : '';
    const parts = valueStr.split(',');
    const taskType = parts[0] || '';
    const startDate = parts[1] || '';
    const endDate = parts[2] || '';
    
    // In TriSysWebAPI mode, use TaskTypeDropdown component
    // Otherwise fall back to simple dropdown with provided values
    const useTaskTypeComponent = this.options.mode === 'TriSysWebAPI';
    
    if (useTaskTypeComponent) {
      // TaskTypeDropdown component - container will be initialized async
      return `
        <div class="flex items-center gap-2 flex-wrap">
          <!-- TaskTypeDropdown component -->
          <div class="tasktype-dropdown-container" data-tasktype-path="${path}" data-tasktype-value="${this.escapeHtml(taskType)}"></div>
          
          <!-- Start date - DatePicker component -->
          <div class="datepicker-container" data-datepicker-path="${path}" data-datepicker-role="task-start" data-datepicker-value="${startDate}"></div>
          
          <span class="text-base-content/50 font-medium">and</span>
          
          <!-- End date - DatePicker component -->
          <div class="datepicker-container" data-datepicker-path="${path}" data-datepicker-role="task-end" data-datepicker-value="${endDate}"></div>
        </div>
      `;
    } else {
      // Custom mode - use simple dropdown with field values
      const taskTypes = field.values || [];
      const selectedTaskType = taskTypes.find(t => t.value === taskType);
      const selectedLabel = selectedTaskType?.label || 'Select Task Type...';
      const selectedImageUrl = taskType ? this.getTaskTypeImageUrl(taskType) : '';
      
      return `
        <div class="flex items-center gap-2 flex-wrap">
          <!-- Task Type selector with images (Custom mode) -->
          <div class="dropdown" data-task-dropdown="${path}">
            <div tabindex="0" role="button" class="btn btn-sm btn-outline gap-2 min-w-48 justify-start" data-action="toggle-task-dropdown" data-path="${path}">
              ${selectedImageUrl ? `<img src="${selectedImageUrl}" alt="" class="w-4 h-4" onerror="this.style.display='none'">` : '<span class="w-4"></span>'}
              <span class="flex-1 text-left">${selectedLabel}</span>
              <i class="fas fa-chevron-down text-xs"></i>
            </div>
            <ul tabindex="0" class="dropdown-content dropdown-scrollable bg-base-100 rounded-box z-50 max-h-96 overflow-y-auto shadow-lg border border-base-300 p-1" style="display: flex; flex-direction: column; width: max-content;">
              <li class="list-none">
                <a class="flex items-center gap-2 px-2 py-1.5 text-sm whitespace-nowrap hover:bg-base-200 rounded cursor-pointer ${!taskType ? 'bg-primary/20' : ''}" 
                   data-action="select-task-type" data-path="${path}" data-value="">
                  <span class="w-4"></span>
                  <span>Select Task Type...</span>
                </a>
              </li>
              ${taskTypes.map(t => `
                <li class="list-none">
                  <a class="flex items-center gap-2 px-2 py-1.5 text-sm whitespace-nowrap hover:bg-base-200 rounded cursor-pointer ${t.value === taskType ? 'bg-primary/20' : ''}" 
                     data-action="select-task-type" data-path="${path}" data-value="${t.value}">
                    <img src="${this.getTaskTypeImageUrl(String(t.value))}" alt="" class="w-4 h-4" onerror="this.style.display='none'">
                    <span>${t.label}</span>
                  </a>
                </li>
              `).join('')}
            </ul>
          </div>
          
          <!-- Start date - DatePicker component -->
          <div class="datepicker-container" data-datepicker-path="${path}" data-datepicker-role="task-start" data-datepicker-value="${startDate}"></div>
          
          <span class="text-base-content/50 font-medium">and</span>
          
          <!-- End date - DatePicker component -->
          <div class="datepicker-container" data-datepicker-path="${path}" data-datepicker-role="task-end" data-datepicker-value="${endDate}"></div>
        </div>
      `;
    }
  }
  
  /**
   * Render Address Radius field input
   * Value format: "distance,postcode" (e.g., "3,cb1")
   * PostCodeRadial in data object mirrors the value
   */
  private renderAddressRadiusInput(rule: BooleanSearchRule, path: string): string {
    // Parse current value: "distance,postcode"
    const valueStr = typeof rule.value === 'string' ? rule.value : '';
    const parts = valueStr.split(',');
    const distance = parts[0] || '0';
    const postcode = parts[1] || '';
    
    return `
      <div class="flex items-center gap-2">
        <input type="number" 
               class="input input-sm input-bordered w-20 text-center" 
               value="${this.escapeHtml(distance)}"
               min="0" 
               max="75" 
               step="1"
               data-action="change-radius-distance" 
               data-path="${path}">
        <span class="text-base-content/70 text-sm">miles of</span>
        <input type="text" 
               class="input input-sm input-bordered w-20 uppercase" 
               value="${this.escapeHtml(postcode)}"
               maxlength="4"
               placeholder="CB1"
               data-action="change-radius-postcode" 
               data-path="${path}">
      </div>
    `;
  }

  /**
   * Render CurrencyAmountPeriod field input
   * Value format: "currencySymbol;amount;periodName" (e.g., "£;50000;Annum")
   * This matches the Apex DefaultValue format for CurrencyAmountPeriod fields
   */
  private renderCurrencyAmountPeriodInput(rule: BooleanSearchRule, path: string): string {
    // Parse current value: "£5000 per Annum" format (Apex format)
    const valueStr = typeof rule.value === 'string' ? rule.value : '';
    
    // Parse "£5000 per Annum" format using regex
    // Match: currency symbol, amount, "per", period name
    const match = valueStr.match(/^([^\d\s]+)?\s*(\d+(?:\.\d+)?)\s*per\s+(.+)$/i);
    let currencySymbol = '£';
    let amount = '0';
    let periodName = 'Annum';
    
    if (match) {
      currencySymbol = match[1]?.trim() || '£';
      amount = match[2] || '0';
      periodName = match[3]?.trim() || 'Annum';
    } else if (valueStr) {
      // Fallback: try to extract just a number
      const numMatch = valueStr.match(/(\d+(?:\.\d+)?)/); 
      if (numMatch) amount = numMatch[1];
    }
    
    // Render a container that will be populated by CurrencyAmountPeriod component
    return `
      <div class="currency-amount-period-container" 
           data-currency-path="${path}" 
           data-currency-symbol="${this.escapeHtml(currencySymbol)}"
           data-currency-amount="${this.escapeHtml(amount)}"
           data-currency-period="${this.escapeHtml(periodName)}">
        <!-- CurrencyAmountPeriod component will be initialized here -->
        <div class="flex items-center gap-1 text-base-content/50 text-sm">
          <i class="fas fa-spinner fa-spin"></i>
          Loading...
        </div>
      </div>
    `;
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get HTML input type for field type
   */
  private getHtmlInputType(type: FieldType): string {
    switch (type) {
      case 'integer':
      case 'double':
        return 'number';
      case 'date':
        return 'date';
      case 'time':
        return 'time';
      case 'datetime':
        return 'datetime-local';
      default:
        return 'text';
    }
  }

  /**
   * Get the image URL for a TaskType
   * @param taskTypeName - The name of the task type
   * @returns The URL to the task type icon image
   */
  private getTaskTypeImageUrl(taskTypeName: string): string {
    if (!taskTypeName) return '';
    // URL encode the task type name for the path
    const encodedName = encodeURIComponent(taskTypeName);
    return `https://apex.trisys.co.uk/images/trisys/16x16/tasktype/${encodedName}.png`;
  }

  /**
   * Initialize all DatePicker components after render
   * Finds all datepicker-container elements and creates DatePicker instances
   */
  private async initializeDatePickers(): Promise<void> {
    // Clean up existing DatePicker instances
    this.destroyDatePickers();
    
    // Find all datepicker containers
    const containers = this.container.querySelectorAll('.datepicker-container');
    
    for (const containerEl of containers) {
      const path = containerEl.getAttribute('data-datepicker-path') || '';
      const valueStr = containerEl.getAttribute('data-datepicker-value') || '';
      const index = containerEl.getAttribute('data-datepicker-index');
      const role = containerEl.getAttribute('data-datepicker-role'); // task-start or task-end
      
      // Parse the date value
      let dateValue: Date | null = null;
      if (valueStr) {
        const parsed = new Date(valueStr);
        if (!isNaN(parsed.getTime())) {
          dateValue = parsed;
        }
      }
      
      // Generate a unique key for this DatePicker
      let pickerKey = path;
      if (index !== null) {
        pickerKey = `${path}:${index}`;
      } else if (role) {
        pickerKey = `${path}:${role}`;
      }
      
      // Create and initialize the DatePicker
      const datePicker = new DatePicker(containerEl as HTMLElement, {
        name: pickerKey,
        value: dateValue,
        format: 'DD-MMM-YYYY',
        size: 'sm',
        showTodayButton: true,
        showClearButton: true,
        placeholder: 'Select date...',
        onChange: (event) => {
          this.handleDatePickerChange(pickerKey, path, index, role, event.newValue);
        }
      });
      
      await datePicker.initialize();
      this.datePickerInstances.set(pickerKey, datePicker);
    }
    
    logger.debug('DatePickers initialized', { count: this.datePickerInstances.size });
  }

  /**
   * Handle DatePicker value change
   */
  private handleDatePickerChange(
    pickerKey: string, 
    path: string, 
    index: string | null, 
    role: string | null, 
    newValue: Date | null
  ): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;
    
    // Format date as ISO string for storage
    const formattedValue = newValue ? newValue.toISOString().split('T')[0] : '';
    
    if (role === 'task-start' || role === 'task-end') {
      // Task field: update composite value "TaskType,startDate,endDate"
      const currentValue = typeof rule.value === 'string' ? rule.value : '';
      const parts = currentValue.split(',');
      const taskType = parts[0] || '';
      let startDate = parts[1] || '';
      let endDate = parts[2] || '';
      
      if (role === 'task-start') {
        startDate = formattedValue;
      } else {
        endDate = formattedValue;
      }
      
      rule.value = `${taskType},${startDate},${endDate}`;
    } else if (index !== null) {
      // Between operator: update array value
      const values = Array.isArray(rule.value) ? [...rule.value] : ['', ''];
      values[parseInt(index, 10)] = formattedValue;
      rule.value = values;
    } else {
      // Single date value
      rule.value = formattedValue;
    }
    
    this.notifyChange();
  }

  /**
   * Destroy all DatePicker instances
   */
  private destroyDatePickers(): void {
    for (const datePicker of this.datePickerInstances.values()) {
      datePicker.destroy();
    }
    this.datePickerInstances.clear();
  }

  /**
   * Destroy all TaskTypeDropdown instances
   */
  private destroyTaskTypeDropdowns(): void {
    for (const dropdown of this.taskTypeDropdownInstances.values()) {
      dropdown.destroy();
    }
    this.taskTypeDropdownInstances.clear();
  }

  /**
   * Initialize TaskTypeDropdown components for Task fields
   * Called after render() to set up async TaskTypeDropdown instances
   */
  private async initializeTaskTypeDropdowns(): Promise<void> {
    // First destroy any existing instances
    this.destroyTaskTypeDropdowns();
    
    // Find all TaskTypeDropdown containers
    const containers = this.container.querySelectorAll('.tasktype-dropdown-container');
    
    for (const containerEl of containers) {
      const path = containerEl.getAttribute('data-tasktype-path') || '';
      const value = containerEl.getAttribute('data-tasktype-value') || '';
      
      // Create and initialize the TaskTypeDropdown
      const dropdown = new TaskTypeDropdown(containerEl as HTMLElement, {
        name: `tasktype-${path}`,
        value: value,
        placeholder: 'Select Task Type...',
        size: 'sm',
        onChange: (newValue) => {
          this.handleTaskTypeChange(path, newValue);
        }
      });
      
      await dropdown.initialize();
      this.taskTypeDropdownInstances.set(path, dropdown);
    }
    
    logger.debug('TaskTypeDropdowns initialized', { count: this.taskTypeDropdownInstances.size });
  }

  /**
   * Handle TaskTypeDropdown value change
   */
  private handleTaskTypeChange(path: string, newValue: string): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;
    
    // Task field: update composite value "TaskType,startDate,endDate"
    const currentValue = typeof rule.value === 'string' ? rule.value : '';
    const parts = currentValue.split(',');
    const startDate = parts[1] || '';
    const endDate = parts[2] || '';
    
    rule.value = `${newValue},${startDate},${endDate}`;
    
    this.notifyChange();
  }

  /**
   * Initialize CurrencyAmountPeriod components for currency fields
   * Called after render() to set up async CurrencyAmountPeriod instances
   */
  private async initializeCurrencyAmountPeriodComponents(): Promise<void> {
    // First destroy any existing instances
    this.destroyCurrencyAmountPeriodComponents();
    
    // Only works in TriSysWebAPI mode with standingData available
    if (this.options.mode !== 'TriSysWebAPI' || !this.options.standingData) {
      return;
    }
    
    const standingData = this.options.standingData;
    const currencies = standingData.Currencies || [];
    const periods = standingData.ConsultancyPeriods || [];
    
    if (currencies.length === 0 || periods.length === 0) {
      logger.warn('Cannot initialize CurrencyAmountPeriod - missing Currencies or ConsultancyPeriods in standing data');
      return;
    }
    
    // Find all CurrencyAmountPeriod containers
    const containers = this.container.querySelectorAll('.currency-amount-period-container');
    
    for (const containerEl of containers) {
      const path = containerEl.getAttribute('data-currency-path') || '';
      const symbol = containerEl.getAttribute('data-currency-symbol') || '£';
      const amount = parseFloat(containerEl.getAttribute('data-currency-amount') || '0');
      const periodName = containerEl.getAttribute('data-currency-period') || 'Annum';
      
      // Find currency and period IDs from symbols/names
      const currency = currencies.find(c => c.Symbol === symbol) || currencies[0];
      const period = periods.find(p => p.Name === periodName) || periods[0];
      
      if (!currency || !period) {
        logger.warn('Cannot initialize CurrencyAmountPeriod - could not find currency or period', { path, symbol, periodName });
        continue;
      }
      
      // Create and initialize the CurrencyAmountPeriod component
      const component = new CurrencyAmountPeriod(containerEl as HTMLElement, {
        name: `currency-${path}`,
        currencies: currencies,
        periods: periods,
        currencyId: currency.CurrencyId,
        amount: amount,
        periodId: period.ConsultancyPeriodId,
        size: 'sm',
        onChange: (value: CurrencyAmountPeriodValue) => {
          this.handleCurrencyAmountPeriodChange(path, value);
        }
      });
      
      await component.initialize();
      this.currencyAmountPeriodInstances.set(path, component);
    }
    
    logger.debug('CurrencyAmountPeriod components initialized', { count: this.currencyAmountPeriodInstances.size });
  }

  /**
   * Handle CurrencyAmountPeriod value change
   */
  private handleCurrencyAmountPeriodChange(path: string, value: CurrencyAmountPeriodValue): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;
    
    // Store as "£5000 per Annum" format (matches Apex SearchTreeJson format)
    rule.value = `${value.symbol}${value.amount} per ${value.periodName}`;
    
    this.notifyChange();
  }

  /**
   * Destroy all CurrencyAmountPeriod instances
   */
  private destroyCurrencyAmountPeriodComponents(): void {
    for (const component of this.currencyAmountPeriodInstances.values()) {
      component.destroy();
    }
    this.currencyAmountPeriodInstances.clear();
  }

  /**
   * Bind event handlers
   */
  private bindEvents(): void {
    // First unbind any existing handlers to prevent duplicates
    this.unbindEvents();
    
    // Create bound handlers
    this.boundClickHandler = (e) => this.handleClick(e);
    this.boundChangeHandler = (e) => this.handleChange(e);
    this.boundInputHandler = (e) => this.handleInput(e);
    
    // Attach handlers
    this.container.addEventListener('click', this.boundClickHandler);
    this.container.addEventListener('change', this.boundChangeHandler);
    this.container.addEventListener('input', this.boundInputHandler);
  }

  /**
   * Handle click events
   */
  private handleClick(e: Event): void {
    const target = e.target as HTMLElement;
    const button = target.closest('[data-action]') as HTMLElement;
    if (!button) return;

    // Prevent event from bubbling to parent handlers
    e.stopPropagation();

    const action = button.dataset.action;
    const path = button.dataset.path ?? '';

    switch (action) {
      case 'add-field':
        this.addRule(path || this.selectedGroupPath);
        break;
      case 'add-group':
        this.addGroup(path || this.selectedGroupPath);
        break;
      case 'select-group':
        this.selectGroup(path);
        break;
      case 'delete-rule':
        this.deleteRule(path);
        break;
      case 'delete-group':
        this.deleteGroup(path);
        break;
      case 'set-condition':
        this.setCondition(path, button.dataset.condition as 'AND' | 'OR');
        break;
      case 'remove-chip':
        this.removeChip(path, button.dataset.value!);
        break;
      case 'clear-chips':
        this.clearChips(path);
        break;
      case 'select-task-type':
        // Task field: select task type from dropdown menu
        const taskValue = button.dataset.value || '';
        this.updateTaskFieldValue(path, 'type', taskValue);
        // Close the dropdown by removing focus
        const dropdown = button.closest('.dropdown');
        if (dropdown) {
          (dropdown.querySelector('[tabindex]') as HTMLElement)?.blur();
        }
        break;
      case 'search':
        this.triggerSearch();
        break;
      case 'clear':
        // If searching, cancel the search instead of clearing
        if (this.isSearching) {
          this.cancelSearch();
        } else {
          this.clear();
        }
        break;
    }
  }

  /**
   * Handle change events (selects)
   */
  private handleChange(e: Event): void {
    const target = e.target as HTMLSelectElement | HTMLInputElement;
    if (!target.dataset.action) return;

    const action = target.dataset.action;
    const path = target.dataset.path!;

    switch (action) {
      case 'change-field':
        this.changeField(path, (target as HTMLSelectElement).value);
        break;
      case 'change-operator':
        this.changeOperator(path, (target as HTMLSelectElement).value as BooleanOperator);
        break;
      case 'change-value':
        const index = target.dataset.index ? parseInt(target.dataset.index) : undefined;
        this.changeValue(path, target.value, index);
        break;
      case 'add-chip':
        if (target.value) {
          this.addChip(path, target.value);
          target.value = '';
        }
        break;
      case 'change-boolean-value':
        // Boolean checkbox handler
        this.changeBooleanValue(path, (target as HTMLInputElement).checked);
        break;
      case 'change-radius-distance':
        // Address Radius distance handler
        this.changeRadiusValue(path, (target as HTMLInputElement).value, 'distance');
        break;
      case 'change-radius-postcode':
        // Address Radius postcode handler
        this.changeRadiusValue(path, (target as HTMLInputElement).value, 'postcode');
        break;
    }
  }

  /**
   * Handle input events (for immediate value updates)
   */
  private handleInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    const action = target.dataset.action;
    
    if (!action) return;
    
    const path = target.dataset.path!;

    switch (action) {
      case 'change-value': {
        const index = target.dataset.index ? parseInt(target.dataset.index) : undefined;
        this.changeValue(path, target.value, index);
        break;
      }
      case 'change-radius-distance':
        this.changeRadiusValue(path, target.value, 'distance');
        break;
      case 'change-radius-postcode':
        this.changeRadiusValue(path, target.value, 'postcode');
        break;
    }
  }

  /**
   * Navigate to a group/rule by path
   */
  private getByPath(path: string): BooleanSearchRule | BooleanSearchGroup | null {
    if (!path) return this.tree;
    
    const parts = path.split('.').map(Number);
    let current: BooleanSearchRule | BooleanSearchGroup = this.tree;
    
    for (const idx of parts) {
      if (!('rules' in current)) return null;
      current = current.rules[idx];
      if (!current) return null;
    }
    
    return current;
  }

  /**
   * Get parent group and index for a path
   */
  private getParentAndIndex(path: string): { parent: BooleanSearchGroup; index: number } | null {
    const parts = path.split('.').map(Number);
    const index = parts.pop()!;
    
    if (parts.length === 0) {
      return { parent: this.tree, index };
    }
    
    const parentPath = parts.join('.');
    const parent = this.getByPath(parentPath);
    
    if (!parent || !('rules' in parent)) return null;
    return { parent, index };
  }

  /**
   * Select a group for adding new fields/groups to
   */
  private selectGroup(path: string): void {
    // Toggle selection - clicking same group deselects it
    if (this.selectedGroupPath === path) {
      this.selectedGroupPath = '';
    } else {
      this.selectedGroupPath = path;
    }
    this.reRenderSync();
  }

  /**
   * Add a new rule
   */
  private addRule(groupPath: string): void {
    const group = this.getByPath(groupPath) as BooleanSearchGroup;
    if (!group || !('rules' in group)) return;

    const visibleFields = this.getVisibleFields();
    const defaultField = visibleFields[0];
    if (!defaultField) return;

    const newRule: BooleanSearchRule = {
      id: defaultField.id,
      field: defaultField.id,
      type: defaultField.type,
      input: defaultField.input || 'text',
      operator: defaultField.default_operator || this.getOperatorsForField(defaultField, !!(defaultField.values?.length))[0],
      value: defaultField.default_value ?? (defaultField.values?.length ? [] : '')
    };

    group.rules.push(newRule);
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Add a new nested group
   */
  private addGroup(parentPath: string): void {
    const parent = this.getByPath(parentPath) as BooleanSearchGroup;
    if (!parent || !('rules' in parent)) return;

    const newGroup: BooleanSearchGroup = {
      condition: parent.condition === 'AND' ? 'OR' : 'AND',
      rules: []
    };

    parent.rules.push(newGroup);
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Delete a rule
   */
  private deleteRule(path: string): void {
    const result = this.getParentAndIndex(path);
    if (!result) return;

    result.parent.rules.splice(result.index, 1);
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Delete a group
   */
  private deleteGroup(path: string): void {
    this.deleteRule(path); // Same logic as deleting a rule
  }

  /**
   * Set group condition (AND/OR)
   */
  private setCondition(path: string, condition: 'AND' | 'OR'): void {
    const group = this.getByPath(path) as BooleanSearchGroup;
    if (!group || !('rules' in group)) return;

    group.condition = condition;
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Change field for a rule
   */
  private changeField(path: string, fieldId: string): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    const field = this.getField(fieldId);
    if (!field) return;

    rule.id = field.id;
    rule.field = field.id;
    rule.type = field.type;
    rule.input = field.input || 'text';
    rule.operator = field.default_operator || this.getOperatorsForField(field, !!(field.values?.length))[0];
    rule.value = field.default_value ?? (field.values?.length ? [] : '');

    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Change operator for a rule
   */
  private changeOperator(path: string, operator: BooleanOperator): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    const oldOperator = rule.operator;
    rule.operator = operator;

    // Reset value if operator type changes significantly
    const oldNbInputs = OPERATORS[oldOperator].nb_inputs;
    const newNbInputs = OPERATORS[operator].nb_inputs;
    
    if (oldNbInputs !== newNbInputs) {
      if (newNbInputs === 0) {
        rule.value = null;
      } else if (newNbInputs === 2) {
        rule.value = ['', ''];
      } else if (operator === 'in' || operator === 'not_in') {
        rule.value = [];
      } else {
        rule.value = '';
      }
    }

    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Change value for a rule
   */
  private changeValue(path: string, value: string, index?: number): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    if (index !== undefined && Array.isArray(rule.value)) {
      rule.value[index] = value;
    } else {
      // Convert to appropriate type
      if (rule.type === 'integer') {
        rule.value = value ? parseInt(value) : '';
      } else if (rule.type === 'double') {
        rule.value = value ? parseFloat(value) : '';
      } else if (rule.type === 'boolean') {
        rule.value = value === 'true';
      } else {
        rule.value = value;
      }
    }

    this.notifyChange();
  }
  
  /**
   * Change boolean value for a rule (checkbox handler)
   */
  private changeBooleanValue(path: string, checked: boolean): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    rule.value = checked;
    
    // Update the label text next to checkbox
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Change Address Radius value
   * Value format: "distance,postcode" (e.g., "3,cb1")
   * Also populates data.PostCodeRadial to match Apex format
   * 
   * @param path - Rule path
   * @param value - The new value for that part
   * @param part - Which part to update: 'distance' or 'postcode'
   */
  private changeRadiusValue(path: string, value: string, part: 'distance' | 'postcode'): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    // Parse current value: "distance,postcode"
    const currentValue = typeof rule.value === 'string' ? rule.value : '';
    const parts = currentValue.split(',');
    let distance = parts[0] || '0';
    let postcode = parts[1] || '';

    if (part === 'distance') {
      // Clamp distance to 0-75 range
      const numVal = parseInt(value) || 0;
      distance = String(Math.max(0, Math.min(75, numVal)));
    } else {
      // Uppercase and limit postcode to 4 characters
      postcode = value.toUpperCase().substring(0, 4);
    }

    // Combine into "distance,postcode" format
    const newValue = `${distance},${postcode}`;
    rule.value = newValue;

    // Add data.PostCodeRadial to match Apex format
    rule.data = {
      PostCodeRadial: newValue
    };

    this.notifyChange();
  }
  
  /**
   * Update Task field composite value
   * Value format: "TaskTypeName,startDate,endDate"
   * 
   * @param path - Rule path
   * @param part - Which part to update: 'type', 'startDate', or 'endDate'
   * @param value - The new value for that part
   */
  private updateTaskFieldValue(path: string, part: 'type' | 'startDate' | 'endDate', value: string): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    // Parse current value: "TaskTypeName,startDate,endDate"
    const currentValue = typeof rule.value === 'string' ? rule.value : '';
    const parts = currentValue.split(',');
    let taskType = parts[0] || '';
    let startDate = parts[1] || '';
    let endDate = parts[2] || '';
    
    // Update the specified part
    switch (part) {
      case 'type':
        taskType = value;
        break;
      case 'startDate':
        startDate = value;
        break;
      case 'endDate':
        endDate = value;
        break;
    }
    
    // Rebuild the composite value
    rule.value = `${taskType},${startDate},${endDate}`;
    
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Add chip (multi-select value)
   */
  private addChip(path: string, value: string): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    if (!Array.isArray(rule.value)) {
      rule.value = [];
    }
    
    if (!rule.value.includes(value)) {
      rule.value.push(value);
    }

    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Remove chip (multi-select value)
   */
  private removeChip(path: string, value: string): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule || !Array.isArray(rule.value)) return;

    rule.value = rule.value.filter(v => v !== value);
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Clear all chips
   */
  private clearChips(path: string): void {
    const rule = this.getByPath(path) as BooleanSearchRule;
    if (!rule || 'condition' in rule) return;

    rule.value = [];
    this.reRenderSync();
    this.notifyChange();
  }

  /**
   * Notify of tree change
   */
  private notifyChange(): void {
    if (this.options.autoSearch) {
      this.debouncedSearch();
    }
    this.options.onChange?.(this.getSearchTree());
  }

  /**
   * Debounced search trigger
   */
  private debouncedSearch(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.triggerSearch();
    }, this.options.debounceDelay);
  }

  /**
   * Trigger search callback
   */
  private triggerSearch(): void {
    const tree = this.getSearchTree();
    logger.debug('Search triggered', { tree });
    this.options.onSearch?.(tree);
  }
  
  /**
   * Set loading state - shows spinner and disables search button
   * Call this from consuming form when search starts
   */
  public setLoading(loading: boolean): void {
    this.isSearching = loading;
    
    const searchBtn = this.container.querySelector('[data-search-button]') as HTMLButtonElement;
    const searchIcon = this.container.querySelector('[data-search-icon]') as HTMLElement;
    const searchSpinner = this.container.querySelector('[data-search-spinner]') as HTMLElement;
    const searchText = this.container.querySelector('[data-search-text]') as HTMLElement;
    const clearBtn = this.container.querySelector('[data-clear-button]') as HTMLButtonElement;
    const clearText = this.container.querySelector('[data-clear-text]') as HTMLElement;
    
    if (loading) {
      // Disable search button and show spinner
      if (searchBtn) {
        searchBtn.disabled = true;
        searchBtn.classList.add('btn-disabled');
      }
      if (searchIcon) searchIcon.classList.add('hidden');
      if (searchSpinner) searchSpinner.classList.remove('hidden');
      if (searchText) searchText.textContent = 'Searching...';
      
      // Change clear button to cancel
      if (clearText) clearText.textContent = 'Cancel Search';
      
      // Create abort controller for cancellation
      this.searchAbortController = new AbortController();
    } else {
      // Re-enable search button and hide spinner
      if (searchBtn) {
        searchBtn.disabled = false;
        searchBtn.classList.remove('btn-disabled');
      }
      if (searchIcon) searchIcon.classList.remove('hidden');
      if (searchSpinner) searchSpinner.classList.add('hidden');
      if (searchText) searchText.textContent = 'Search/Lookup';
      
      // Restore clear button text
      if (clearText) clearText.textContent = 'Clear Search';
      
      // Clear abort controller
      this.searchAbortController = null;
    }
  }
  
  /**
   * Check if a search is currently in progress
   */
  public isLoading(): boolean {
    return this.isSearching;
  }
  
  /**
   * Get the abort signal for the current search (for fetch cancellation)
   */
  public getAbortSignal(): AbortSignal | undefined {
    return this.searchAbortController?.signal;
  }
  
  /**
   * Cancel the current search if one is in progress
   */
  public cancelSearch(): void {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      logger.debug('Search cancelled by user');
    }
    this.setLoading(false);
  }
}
